import React, {useState} from 'react';
import {Field, FieldProp} from '../../types';

import {NestedForm} from './NestedForm';
import {Modal} from '../Modal';
import {Form, FormState, FormStateCallback, initialFieldState, initialFormState} from '../Form';
import {FieldContainerTableCell} from '../FieldContainerTableCell';
import {ActivityButton} from '../ActivityButton';
import {Tooltip} from '../Tooltip';

import {applyVisibilityRules, filterDisplayFields, parseMergeTag, validate} from '../../_helpers/form';

type NestedFormGridModalProps = FieldProp & {
    additionalButtons: JSX.Element[] | undefined;   // additional list of buttons to display next to "Add ..."
                                                    // it is the parent's responsibility to handle any button interactions
};

export const NestedFormGridModal: React.FunctionComponent<NestedFormGridModalProps> = ({
                                                                                           field,
                                                                                           value,
                                                                                           displayOnly,
                                                                                           onChange,
                                                                                           form,
                                                                                           showAdministrativeFields = false,
                                                                                           showPrivateFields = false,
                                                                                           ...rest
                                                                                       }): JSX.Element => {

    const [showBulkEditModal, setShowBulkEditModal] = useState<boolean>(false);
    const [bulkEditFormState, setBulkEditFormState] = useState<FormState[]>([]);
    const [formIsSaving, setFormIsSaving] = useState<boolean>(false);

    const addNewRow = () => {
        const fieldValueDefaults = field.nestedForm.fields.reduce((acc: any, field: Field) => ({
            ...acc,
            [field.name]: parseMergeTag(field.initialValue, form, rest.formState)
        }), {});
        const fieldStateDefaults = field.nestedForm.fields.reduce((acc: any, field: Field) => ({
            ...acc,
            [field.name]: {...initialFieldState}
        }), {});

        setBulkEditFormState([
            ...bulkEditFormState,
            {
                ...initialFormState,
                fieldValues: fieldValueDefaults,
                fieldState: fieldStateDefaults,
                visibility: applyVisibilityRules(field.nestedForm, undefined, {
                    ...initialFormState,
                    fieldValues: fieldValueDefaults,
                }, false, false),
            }
        ]);
    };

    const handleBulkAddAndEdit = () => {
        if (!displayOnly && value.length === 0) {
            addNewRow();
        } else {
            // take each value of NestedForm and convert to FormState
            const state: FormState[] = value.map((item: any) => ({
                ...initialFormState,
                fieldValues: {...item},
                visibility: applyVisibilityRules(field.nestedForm, undefined, {
                    ...initialFormState,
                    fieldValues: {...item}
                }, showAdministrativeFields, showPrivateFields),
                fieldState: Object.keys(item).reduce((acc: any, val: any) => ({
                    ...acc,
                    [val]: {...initialFieldState}
                }), {})
            }));

            setBulkEditFormState(state);
        }

        setShowBulkEditModal(true);
    };

    // @ts-ignore
    const localDisplayOnly: boolean = field.hasOwnProperty('gpnfDisableEdit') ? field.gpnfDisableEdit : displayOnly;

    // build a list of distinct section headings to be used in the table header
    const sectionHeaders = field.gpnfFields.reduce((acc: any, fieldId: any) => {
        const nestedField: Field = field.nestedForm.fieldMap.hasOwnProperty(fieldId) ? field.nestedForm.fieldMap[fieldId] : null;

        if (nestedField.visibility === 'hidden' || (nestedField.visibility === 'administrative' && !showAdministrativeFields)) {
            return acc;
        }

        const sectionFieldId = nestedField.sectionFieldId || 0;
        const matchedSectionIndex = acc.findIndex((o: any) => o.fieldId === sectionFieldId);

        // if no fields in column are visible, then column is not visible
        const shouldShowColumn = bulkEditFormState.some((state: any) => (
            state.visibility.hasOwnProperty('input_' + nestedField.id) &&
            state.visibility['input_' + nestedField.id]
        ));

        if (matchedSectionIndex >= 0) {
            acc[matchedSectionIndex].count++;
            if (!shouldShowColumn) {
                acc[matchedSectionIndex].hidden++;
            }
            return acc;
        } else {
            const sectionField = field.nestedForm.fieldMap.hasOwnProperty(sectionFieldId) ?
                {fieldId: field.nestedForm.fieldMap[sectionFieldId].id, title: field.nestedForm.fieldMap[sectionFieldId].label, hidden: 0, count: 0}
                :
                {fieldId: sectionFieldId, title: '', count: 0, hidden: 0};

            sectionField.count++;
            if (!shouldShowColumn) {
                sectionField.hidden++;
            }
            return [
                ...acc,
                sectionField
            ];
        }
    }, []);

    return (
        <div className="field-nestedformgridmodal">
            <NestedForm
                {...rest}
                form={form}
                displayOnly={localDisplayOnly}
                field={field}
                value={value}
                onChange={onChange}
                showAdministrativeFields={showAdministrativeFields}
                showPrivateFields={showPrivateFields}
                additionalButtons={[
                    <button key="invoice-search-button"
                            className="btn btn-sm btn-secondary ml-2"
                            onClick={handleBulkAddAndEdit}
                    >
                        Bulk Add / Edit
                    </button>
                ]}
            />
            {localDisplayOnly && field.hasOwnProperty('gpnfEditableFields') && field?.gpnfEditableFields?.length > 0 &&
            <button type="button"
                    className="btn btn-secondary"
                    onClick={handleBulkAddAndEdit}
            >Edit</button>}
            {showBulkEditModal && <Modal size="xl"
                                         isScrollable={true}
            >
                <div className="modal-header">
                    <h5 className="modal-title">Bulk Edit {field.gpnfEntryLabelPlural}</h5>
                    <button type="button"
                            className="close"
                            tabIndex={-1}
                            onClick={() => {
                                setShowBulkEditModal(false);
                            }}
                    >×
                    </button>
                </div>
                <div className="modal-body">
                    <table className="table table-sm nested-form-grid">
                        <thead>
                        {sectionHeaders.length > 0 &&
                        <tr>
                            <th style={{whiteSpace: 'nowrap', width: '10px', minWidth: '0px'}}/>
                            {sectionHeaders.map((sectionHeader: any, index: number) => (
                                <th key={`section-${sectionHeader.fieldId}`}
                                    colSpan={sectionHeader.count}
                                    className={[
                                        'section-header',
                                        (sectionHeader.count === sectionHeader.hidden) && 'hidden',
                                        index > 0 && 'divider'
                                    ].filter(Boolean).join(' ')}
                                >
                                    <div>{sectionHeader.title}</div>
                                </th>
                            ))}
                        </tr>
                        }
                        <tr>
                            <th style={{whiteSpace: 'nowrap', width: '10px', minWidth: '0px'}}/>
                            {/* TODO: filter gpnfFields by gpnfDisplayFields IF passed */}
                            {field.gpnfFields
                                .map((fieldId: any, index: number) => {
                                    const nestedField: Field = field.nestedForm.fieldMap.hasOwnProperty(fieldId) ? field.nestedForm.fieldMap[fieldId] : null;

                                    if (nestedField.visibility === 'hidden' || (nestedField.visibility === 'administrative' && !showAdministrativeFields)) {
                                        return <></>;
                                    }

                                    // if no fields in column are visible, then column is not visible
                                    const shouldShowColumn = bulkEditFormState.some((state: any) => (
                                        state.visibility.hasOwnProperty('input_' + fieldId) &&
                                        state.visibility['input_' + fieldId]
                                    ));

                                    return nestedField ?
                                        <th key={index}
                                            className={[
                                                field.hasOwnProperty('gpnfEditableFields') &&
                                                field.hasOwnProperty('gpnfHighlightEditableFields') &&
                                                field.gpnfHighlightEditableFields &&
                                                // @ts-ignore
                                                // tslint:disable-next-line:triple-equals
                                                field?.gpnfEditableFields.some((editableFieldId: number) => editableFieldId == fieldId) &&
                                                'highlight-editable',
                                                !shouldShowColumn && 'hidden'
                                            ].filter(Boolean).join(' ')}>
                                            <div>
                                                {nestedField.label}
                                                {nestedField.isRequired && <span className="text-danger">*</span>}
                                                {nestedField.flowHelpText &&
                                                    <Tooltip text={nestedField.flowHelpText}>
                                                        <a href="#" className="flow-help" title="Help"><i
                                                            className="fas fa-question-circle"/></a>
                                                    </Tooltip>
                                                }
                                                {!displayOnly && nestedField.description &&
                                                    <div className="field-description small" dangerouslySetInnerHTML={{__html: field.description}}/>}
                                            </div>
                                        </th>
                                        :
                                        null;
                                })}
                        </tr>
                        </thead>
                        {/* the ternary operator while dumb, removes the "empty text node in table" warning thrown by react: https://stackoverflow.com/a/53519842 */}
                        {bulkEditFormState && bulkEditFormState.length > 0 ? <tbody>
                        {bulkEditFormState.map((state: any, index: number) => (
                            <tr key={index}>
                                <td style={{whiteSpace: 'nowrap', minWidth: '0px', verticalAlign: 'middle'}}>
                                    {!localDisplayOnly &&
                                    <button type="button"
                                            className="btn btn-link"
                                            onClick={() => {
                                                // remove fields that don't belong on a new entry(i.e. id)
                                                const fieldValues = Object.keys(state?.fieldValues).reduce((acc, prop) => {
                                                    if (prop.startsWith('input_') || prop.match(/^[0-9]+$/)) {
                                                        return {
                                                            ...acc,
                                                            [prop]: state.fieldValues[prop]
                                                        }
                                                    }
                                                    return acc;
                                                }, {});

                                                setBulkEditFormState([
                                                    ...bulkEditFormState,
                                                    {
                                                        ...state,
                                                        fieldValues
                                                    }
                                                ]);
                                            }}
                                    ><i className="far fa-clone" style={{color: '#333', fontSize: '150%'}}/>
                                    </button>
                                    }
                                    {!state.fieldValues.id &&
                                    <button type="button"
                                            className="btn btn-link"
                                            onClick={(e) => {
                                                e.preventDefault();

                                                setBulkEditFormState([
                                                    ...bulkEditFormState.slice(0, index),
                                                    ...bulkEditFormState.slice(index + 1)
                                                ])
                                            }}
                                    ><i className="far fa-trash-alt text-danger" style={{fontSize: '150%'}} /></button>
                                    }
                                </td>
                                <Form form={field.nestedForm}
                                      displayOnly={field.hasOwnProperty('gpnfEditableFields')}
                                      editableFieldIds={field?.gpnfEditableFields || field.gpnfFields}
                                      highlightEditableFields={field?.gpnfHighlightEditableFields}
                                      displayFields={field?.gpnfDisplayFields || field.gpnfFields}
                                      displayFieldsMode={field?.gpnfDisplayFieldMode || 'selected_fields'}
                                      formState={state}
                                      setFormState={(stateOrCallback: FormState | FormStateCallback) => {
                                          const newState = (stateOrCallback && {}.toString.call(stateOrCallback) === '[object Function]') ?
                                              // @ts-ignore
                                              stateOrCallback(state)
                                              :
                                              stateOrCallback;

                                          setBulkEditFormState([
                                              ...bulkEditFormState.slice(0, index),
                                              newState,
                                              ...bulkEditFormState.slice(index + 1),
                                          ])
                                      }}
                                      showAdministrativeFields={showAdministrativeFields}
                                      showPrivate={showPrivateFields}
                                      fieldContainerComponent={FieldContainerTableCell}
                                />
                            </tr>
                        ))}
                        </tbody> : null}
                    </table>
                </div>
                <div className="modal-footer">
                    {!localDisplayOnly &&
                    <button type="button"
                            className="btn btn-secondary"
                            disabled={formIsSaving}
                            onClick={addNewRow}
                    >Add Row
                    </button>
                    }
                    <div className="flex-grow-1"/>
                    <button type="button"
                            className="btn btn-secondary"
                            disabled={formIsSaving}
                            onClick={() => {
                                setShowBulkEditModal(false);
                            }}
                    >Cancel
                    </button>
                    <ActivityButton
                        type="button"
                        isActive={formIsSaving}
                        className="btn btn-primary"
                        onClick={() => {
                            setFormIsSaving(true);

                            // validate each of the items
                            let hasValidationError = false;
                            bulkEditFormState.forEach((state: FormState, index: number) => {
                                // @ts-ignore
                                const filterFields: any = field?.gpnfEditableFields?.length > 0 ? field.gpnfEditableFields.map((field: any) => parseInt(field, 10).toString()) : [];

                                const isValid = validate(field.nestedForm, state, (stateOrCallback: FormState | FormStateCallback) => {
                                    const newState = (stateOrCallback && {}.toString.call(stateOrCallback) === '[object Function]') ?
                                        // @ts-ignore
                                        stateOrCallback(state)
                                        :
                                        stateOrCallback;

                                    setBulkEditFormState([
                                        ...bulkEditFormState.slice(0, index),
                                        newState,
                                        ...bulkEditFormState.slice(index + 1)
                                    ]);
                                }, filterFields);

                                if (!isValid) {
                                    hasValidationError = true;
                                }
                            });

                            setFormIsSaving(false);

                            if (!hasValidationError) {
                                // update parent form state
                                onChange && onChange(bulkEditFormState.map((itemState: FormState) => ({
                                    ...itemState.fieldValues
                                })));

                                setShowBulkEditModal(false);
                            }
                        }}
                    >Save
                    </ActivityButton>
                </div>
            </Modal>}
        </div>
    );
};